'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import Image from 'next/image'; // Import Next.js Image component

// List of images for the animation cycle (highlights)
const HIGHLIGHT_IMAGE_FILENAMES = [
  '01_manufacturing',
  '02_professional',
  '03_science',
  '04_energy',
  '05_software',
  '06_wholesale',
  '07_retail',
  '08_finance',
];

// Static base image (no highlights)
const BASE_IMAGE_FILENAME = '00_base.jpg';

interface IndustryWheelProps {
  id: string; // e.g., "manufacturing", "financial"
}

const IndustryWheel: React.FC<IndustryWheelProps> = ({ id }) => {
  console.log(`IndustryWheel rendered. id: ${id}`);

  const getInitialIndex = useCallback(() => {
    const idx = HIGHLIGHT_IMAGE_FILENAMES.findIndex((name) => name.substring(3) === id);
    return idx !== -1 ? idx : 0;
  }, [id]);

  const [currentIndex, setCurrentIndex] = useState(getInitialIndex());
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const stepRef = useRef(0);
  const sequenceRef = useRef<number[]>([]);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  console.log(
    `IndustryWheel initial state: currentIndex=${currentIndex}, hasAnimated=${hasAnimated}`
  );

  const animateWheel = useCallback(() => {
    console.log(`animateWheel: Attempting to start. id: ${id}`);

    const totalImages = HIGHLIGHT_IMAGE_FILENAMES.length;
    const targetIndex = HIGHLIGHT_IMAGE_FILENAMES.findIndex((name) => name.substring(3) === id);
    console.log(
      `animateWheel: totalImages=${totalImages}, targetIndex=${targetIndex} (for id '${id}')`
    );

    if (targetIndex === -1) {
      console.error(
        `animateWheel: id '${id}' did not match any image. Setting current image to initial and stopping.`
      );
      setCurrentIndex(getInitialIndex());
      setHasAnimated(true);
      return;
    }

    const localSequence: number[] = [];
    const rotations = 1;
    const startIndexForAnimation = getInitialIndex();
    for (
      let i = 0;
      i <
      rotations * totalImages +
        ((targetIndex - startIndexForAnimation + totalImages) % totalImages) +
        1;
      i++
    ) {
      localSequence.push((startIndexForAnimation + i) % totalImages);
    }
    if (localSequence.length > 0 && localSequence[localSequence.length - 1] !== targetIndex) {
      localSequence.push(targetIndex);
    }
    if (localSequence.length === 0 && targetIndex !== -1) {
      localSequence.push(targetIndex);
    }

    sequenceRef.current = localSequence;
    stepRef.current = 0;
    console.log('animateWheel: Animation sequence generated:', sequenceRef.current);
    console.log('animateWheel: Initial stepRef.current:', stepRef.current);

    const minInterval = 100; // Adjusted to be >= CSS transition
    const maxInterval = 450; // Adjusted for potentially smoother easing
    const easeOut = (t: number) => minInterval + (maxInterval - minInterval) * t * t;

    function nextFrame() {
      if (stepRef.current >= sequenceRef.current.length) {
        console.log(
          'animateWheel: Animation sequence finished. Final index should be:',
          targetIndex
        );
        setCurrentIndex(targetIndex);
        return;
      }

      const imageIndexToShow = sequenceRef.current[stepRef.current];
      console.log(
        `animateWheel.nextFrame: step=${stepRef.current}, Attempting to set currentIndex=${imageIndexToShow}`
      );
      setCurrentIndex(imageIndexToShow);

      stepRef.current++;

      if (stepRef.current < sequenceRef.current.length) {
        const progress = stepRef.current / sequenceRef.current.length;
        const interval = easeOut(progress);
        // Ensure interval is not shorter than CSS transition to prevent visual glitches if possible
        const effectiveInterval = Math.max(interval, 200);
        console.log(
          `animateWheel.nextFrame: Scheduling next frame in ${effectiveInterval}ms. Progress: ${progress.toFixed(2)}`
        );
        if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = setTimeout(nextFrame, effectiveInterval);
      } else {
        console.log(
          'animateWheel.nextFrame: Reached end of sequence. Setting final index to:',
          imageIndexToShow
        );
        setCurrentIndex(imageIndexToShow);
      }
    }

    console.log('animateWheel: Kicking off first frame.');
    if (sequenceRef.current.length > 0) {
      nextFrame();
    } else {
      console.log(
        'animateWheel: Sequence is empty, not starting animation. Setting to targetIndex.'
      );
      setCurrentIndex(targetIndex);
      setHasAnimated(true);
    }
  }, [id, getInitialIndex]);

  // IntersectionObserver to trigger animation once when 50% visible
  useEffect(() => {
    if (!containerRef.current || hasAnimated || !id) {
      console.log(
        `IntersectionObserverEffect: Bypassing observer setup (already animated, no ID, or no containerRef). hasAnimated: ${hasAnimated}, id: ${id}`
      );
      return;
    }

    console.log('IntersectionObserverEffect: Setting up observer.');
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        console.log(
          `IntersectionObserver callback: isIntersecting: ${entry.isIntersecting}, intersectionRatio: ${entry.intersectionRatio}`
        );
        if (entry.isIntersecting && !hasAnimated) {
          console.log(
            'IntersectionObserverEffect: Component is 50% visible and not animated yet. Starting animation.'
          );
          setHasAnimated(true);
          animateWheel();
          observer.disconnect();
        }
      },
      { threshold: 0.5 } // Trigger when 50% of the element is visible
    );

    observer.observe(containerRef.current);

    return () => {
      console.log('IntersectionObserverEffect: Cleaning up observer.');
      observer.disconnect();
    };
  }, [id, hasAnimated, animateWheel]);

  useEffect(() => {
    console.log('useEffect[]: Preloading images (highlights and base)');
    HIGHLIGHT_IMAGE_FILENAMES.forEach((name) => {
      const img = new window.Image();
      img.src = `/industry/${name}.jpg`;
    });
    const baseImg = new window.Image();
    baseImg.src = `/industry/${BASE_IMAGE_FILENAME}`;
  }, []);

  const currentHighlightFilename =
    HIGHLIGHT_IMAGE_FILENAMES[currentIndex] || HIGHLIGHT_IMAGE_FILENAMES[0];
  console.log(
    `IndustryWheel render: Base: ${BASE_IMAGE_FILENAME}, Current Highlight: '${currentHighlightFilename}' (index ${currentIndex})`
  );

  return (
    <div ref={containerRef} className="relative aspect-[1100/1000] w-full">
      <Image
        key={BASE_IMAGE_FILENAME}
        src={`/industry/${BASE_IMAGE_FILENAME}`}
        alt="Industry wheel base"
        fill
        objectFit="cover"
        className="z-0 rounded-lg opacity-100"
        draggable={false}
        priority
      />

      {HIGHLIGHT_IMAGE_FILENAMES.map((name, idx) => (
        <Image
          key={name}
          src={`/industry/${name}.jpg`}
          alt={id}
          fill
          objectFit="cover"
          className={`rounded-lg transition-opacity duration-200 ease-in-out ${currentIndex === idx ? 'z-10 opacity-100' : 'pointer-events-none z-0 opacity-0'}`}
          draggable={false}
          loading="eager"
        />
      ))}
    </div>
  );
};

export default IndustryWheel;
